import { api } from './client'

/**
 * F-079 / TASK-0333 — controlli finanziari: matrice di approvazione delle spese, approvazioni, spese duplicate, alert e
 * soglie di allarme dei soci. Letture: Admin, Amministrazione, Commercialista (FinanceAdministrationRead); scritture:
 * Admin e Amministrazione. Le approvazioni delle singole spese entrano con FinanceStaffWrite e il livello si controlla
 * sul server. Nessuna email, nessuna previsione (DEC-007), nessun credito atteso (DEC-004).
 */

export type ApproverLevel = 'Responsabile' | 'Amministrazione' | 'Admin'

export const APPROVER_LEVELS: { value: ApproverLevel; label: string }[] = [
  { value: 'Responsabile', label: 'Responsabile (direzione o amministrazione)' },
  { value: 'Amministrazione', label: 'Amministrazione' },
  { value: 'Admin', label: 'Solo Admin' },
]

export interface ApprovalPolicy {
  id: number
  name: string
  minAmount: number
  maxAmount: number | null
  expenseCategoryId: number | null
  costCentreId: number | null
  requesterUserId: number | null
  approverLevel: ApproverLevel
  requiredApprovals: 1 | 2
  priority: number
  isActive: boolean
}

export interface ApprovalPolicyBody {
  name: string
  minAmount: number
  maxAmount?: number | null | undefined
  expenseCategoryId?: number | null | undefined
  costCentreId?: number | null | undefined
  requesterUserId?: number | null | undefined
  approverLevel: ApproverLevel
  requiredApprovals: number
  priority: number
  isActive: boolean
}

export interface ApprovalInfo {
  expenseId: number
  status: string
  requesterUserId: number | null
  requirement: {
    policyId: number | null
    policyName: string | null
    level: ApproverLevel
    requiredApprovals: number
    /** true = nessuna regola combacia: vale il clic singolo di sempre. */
    isDefault: boolean
  }
  approvedCount: number
  /** true se chi guarda puo' decidere adesso (livello giusto, non e' il richiedente, non ha gia' deciso). */
  canDecide: boolean
  approvals: { approverUserId: number; decision: 'Approvata' | 'Rifiutata'; comment: string | null; at: string }[]
}

export interface FinanceAlertItem {
  type: 'incasso_non_riconciliato' | 'saldo_socio_oltre_soglia' | 'budget_superato'
  entityType: string
  entityId: number
  key: string
  message: string
  ageDays?: number
  balance?: number
  threshold?: number | null
  budget?: number
  actual?: number
}

export interface PartnerThreshold {
  partnerId: number
  name: string
  threshold: number | null
  balance: number | null
}

export interface AlertsResponse {
  asOf: string
  settings: { unreconciledDays: number; budgetOverPercent: number }
  count: number
  items: FinanceAlertItem[]
  partnerThresholds: PartnerThreshold[]
}

export interface DuplicateGroup {
  expenseIds: number[]
  expenses: {
    id: number
    description: string
    supplierId: number | null
    expenseDate: string
    totalAmount: number
    status: string
  }[]
}

export interface DuplicatesResponse {
  windowDays: number
  days: number
  count: number
  groups: DuplicateGroup[]
}

export const financeControlsApi = {
  policies: () => api<ApprovalPolicy[]>('/api/finance/expense-approval-policies'),
  createPolicy: (body: ApprovalPolicyBody) =>
    api<{ id: number }>('/api/finance/expense-approval-policies', { method: 'POST', body: JSON.stringify(body) }),
  updatePolicy: (id: number, body: ApprovalPolicyBody) =>
    api<void>(`/api/finance/expense-approval-policies/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deletePolicy: (id: number) =>
    api<void>(`/api/finance/expense-approval-policies/${id}`, { method: 'DELETE' }),

  approvals: (expenseId: number) => api<ApprovalInfo>(`/api/expenses/${expenseId}/approvals`),
  decide: (expenseId: number, decision: 'Approvata' | 'Rifiutata', comment?: string) =>
    api<ApprovalInfo>(`/api/expenses/${expenseId}/approvals`, {
      method: 'POST',
      body: JSON.stringify({ decision, comment: comment || undefined }),
    }),

  alerts: (p?: { days?: number | undefined; pct?: number | undefined }) => {
    const q = new URLSearchParams()
    if (p?.days !== undefined) q.set('days', String(p.days))
    if (p?.pct !== undefined) q.set('pct', String(p.pct))
    const qs = q.toString()
    return api<AlertsResponse>(`/api/finance/alerts${qs ? `?${qs}` : ''}`)
  },
  duplicates: (p?: { windowDays?: number | undefined; days?: number | undefined }) => {
    const q = new URLSearchParams()
    if (p?.windowDays !== undefined) q.set('windowDays', String(p.windowDays))
    if (p?.days !== undefined) q.set('days', String(p.days))
    const qs = q.toString()
    return api<DuplicatesResponse>(`/api/finance/expenses/duplicates${qs ? `?${qs}` : ''}`)
  },
  setPartnerThreshold: (partnerId: number, threshold: number | null) =>
    api<void>(`/api/finance/partners/${partnerId}/alert-threshold`, {
      method: 'PUT',
      body: JSON.stringify({ threshold }),
    }),
}
